import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAmendmentCreateEditDispatch, useAmendmentCreateEditState, ACTION_TYPES } from './amendment-create-edit-context';
import ContractAppService from '../../../../application/platform/scp/contract/contract-app-service';
import { useActionRunner } from '../../../../hooks/use-action-runner';
import { hasErrors } from '../../../../utils/validation';
import { IAmendmentAddEditRequest } from '../../../../model/scp/contract/amendment/request/amendment-add-edit-request';
import { IAmendmentResponse } from '../../../../model/scp/contract/amendment/response/amendment-response';
import { validateAmendment } from './contract-validation';

const FIELD_BY_INPUT: Record<string, keyof IAmendmentAddEditRequest> = {
    'select-amendment-type': 'amendmentTypeId',
    'input-amendment-number': 'amendmentNumber',
    'input-amendment-object': 'amendmentObject',
    'input-additional-information': 'additionalInformation',
    'input-initial-value': 'initialValue',
    'input-increase-value': 'increaseValue',
    'input-decrease-value': 'decreaseValue',
    'check-qualification-addition-suppression': 'qualificationAdditionSuppression',
    'check-qualification-validity': 'qualificationValidity',
    'check-qualification-supplier': 'qualificationSupplier',
    'check-qualification-informative': 'qualificationInformative',
    'check-qualification-readjustment': 'qualificationReadjustment',
    'select-supplier-type': 'supplierType',
    'input-supplier-number-identification': 'supplierNumberIdentification',
    'input-supplier-name': 'supplierName',
    'input-informative-observation': 'informativeObservation',
    'input-legal-basis': 'legalBasis',
    'input-number-installments': 'numberInstallments',
    'input-installment-value': 'installmentValue',
    'input-global-value': 'globalValue',
    'input-additional-deadline-days': 'additionalDeadlineDays',
    'input-justification': 'justification',
};

function toFormRequest(data: IAmendmentResponse): IAmendmentAddEditRequest {
    return {
        amendmentTypeId: data.amendmentTypeId,
        amendmentNumber: data.amendmentNumber,
        amendmentObject: data.amendmentObject,
        signDate: data.signDate,
        publicationDate: data.publicationDate,
        effectiveDateStart: data.effectiveDateStart,
        effectiveDateEnd: data.effectiveDateEnd,
        initialValue: data.initialValue,
        increaseValue: data.increaseValue,
        decreaseValue: data.decreaseValue,
        additionalInformation: data.additionalInformation,
        qualificationAdditionSuppression: data.qualificationAdditionSuppression,
        qualificationValidity: data.qualificationValidity,
        qualificationSupplier: data.qualificationSupplier,
        qualificationInformative: data.qualificationInformative,
        qualificationReadjustment: data.qualificationReadjustment,
        supplierNumberIdentification: data.supplierNumberIdentification,
        supplierType: data.supplierType,
        supplierName: data.supplierName,
        supplierNumberIdentificationSubContract: data.supplierNumberIdentificationSubContract,
        supplierTypeSubContract: data.supplierTypeSubContract,
        supplierNameSubContract: data.supplierNameSubContract,
        informativeObservation: data.informativeObservation,
        legalBasis: data.legalBasis,
        numberInstallments: data.numberInstallments,
        installmentValue: data.installmentValue,
        globalValue: data.globalValue,
        additionalDeadlineDays: data.additionalDeadlineDays,
    };
}

export const useAmendmentCreateEditController = () => {
    const state = useAmendmentCreateEditState();
    const dispatch = useAmendmentCreateEditDispatch();
    const navigate = useNavigate();
    const { isSaving, runWithLoading, runSaving, withLoading, showError, showSuccess } = useActionRunner();
    const isEdit = !!state.sequentialId;
    const listRoute = `/contratos/${state.contractId}/aditivos`;

    useEffect(() => {
        if (!isEdit) return;
        runWithLoading('Não foi possível carregar os dados do aditivo', async () => {
            const data = await ContractAppService.getContractAmendment(Number(state.contractId), Number(state.sequentialId));
            dispatch({ type: ACTION_TYPES.setAmendmentRequest, payload: toFormRequest(data) });
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function handleOnChange(e: any) {
        const { name, value, checked, type } = e.target;
        const field = FIELD_BY_INPUT[name];
        if (!field) return;
        dispatch({ type: ACTION_TYPES.setAmendmentRequest, payload: { [field]: type === 'checkbox' ? checked : value } });
    }

    function handleDateChange(field: keyof IAmendmentAddEditRequest, value: string | null) {
        dispatch({ type: ACTION_TYPES.setAmendmentRequest, payload: { [field]: value ?? undefined } });
    }

    function navigateToList() {
        navigate(listRoute);
    }

    function handleSave() {
        if (isSaving) return;
        if (hasErrors(validateAmendment(state.amendmentRequest, isEdit))) {
            dispatch({ type: ACTION_TYPES.setShowValidationErrors, payload: true });
            showError('Preencha todos os campos obrigatórios');
            return;
        }
        runSaving('Não foi possível salvar o aditivo', async () => {
            await withLoading(() => isEdit
                ? ContractAppService.updateContractAmendment(Number(state.contractId), Number(state.sequentialId), state.amendmentRequest)
                : ContractAppService.addContractAmendment(Number(state.contractId), state.amendmentRequest));
            showSuccess(isEdit ? 'Aditivo atualizado com sucesso!' : 'Aditivo adicionado com sucesso!');
            navigateToList();
        });
    }

    return {
        isEdit,
        isSaving,
        listRoute,
        handleOnChange,
        handleDateChange,
        handleSave,
        navigateToList,
    };
};
